import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillGuardEffects } from '../../../types';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-noshow.css';

type Guard = DrillGuardEffects['guards'][number];

const tg = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(`performance:noshowSubstance.guard.${key}`, opts);

const ARM_LABELS: Record<Guard['key'], [string, string]> = {
  card: ['withCard', 'withoutCard'],
  reminder: ['withReminder', 'withoutReminder'],
};

/** The guard's finding, only when the difference passed the significance rule. */
export function guardVerdict(guard: Guard): string {
  if (guard.confounded) return tg('confounded');
  if (!guard.significant || !guard.direction) return tg('noDifference');
  const key = `${guard.key}${guard.direction === 'lower' ? 'Lower' : 'Higher'}`;
  return tg(key, { pts: Math.abs(guard.diffPts).toFixed(1) });
}

function GuardTile({
  guard,
  metricKey,
  minSample,
}: {
  guard: Guard;
  metricKey: DrillGuardEffects['metricKey'];
  minSample: number;
}) {
  const [withKey, withoutKey] = ARM_LABELS[guard.key];
  const arms = [
    { label: tg(withKey), arm: guard.with },
    { label: tg(withoutKey), arm: guard.without },
  ];
  const max = Math.max(guard.with.rate, guard.without.rate, 0.0001);
  const verdict = guardVerdict(guard);
  const n = guard.with.n + guard.without.n;

  const rows = (withEvents: boolean) => (
    <div className="ddrill-ns-rows">
      {arms.map(({ label, arm }) => (
        <div className="ddrill-ns-row" key={label}>
          <span className="dcrux-si-name">{label}</span>
          <span className="dcrux-si-track">
            <span
              className="dcrux-si-fill"
              style={{ width: `${Math.max(2, (arm.rate / max) * 100)}%` }}
            />
          </span>
          <span className="dcrux-si-val">
            {tg('arm', { rate: arm.rate, n: fmt.num(arm.n) })}
          </span>
          {withEvents ? (
            <span className="ddrill-ns-sub">
              {tg('events', { num: fmt.num(arm.num), n: fmt.num(arm.n) })}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );

  const inspector = useMemo(
    () => (
      <>
        {rows(true)}
        <p
          className={`ddrill-ns-verdict${guard.significant ? '' : ' is-quiet'}`}
        >
          {verdict}
        </p>
        {guard.withinChannels ? (
          <p className="ddrill-ns-note">
            {tg('within', {
              pts: guard.withinChannels.diffPts.toFixed(1),
              count: guard.withinChannels.strata,
            })}
          </p>
        ) : null}
        <p className="ddrill-ns-note is-quiet">{tg('caveat')}</p>
        <p className="ddrill-ns-note is-quiet">
          {tg('hidden', { min: minSample })}
        </p>
      </>
    ),
    // rows / verdict derive from guard alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guard, minSample],
  );

  return (
    <DrillTile
      sectionId={`guard:${guard.key}`}
      span={4}
      title={tg(guard.key)}
      meta={tg(metricKey === 'cancel' ? 'metaCancel' : 'metaNoshow', {
        n: fmt.num(n),
      })}
      inspector={inspector}
    >
      {rows(false)}
      <p
        className={`ddrill-ns-verdict${guard.significant ? '' : ' is-quiet'}`}
        data-significant={guard.significant ? 'true' : 'false'}
      >
        {verdict}
      </p>
    </DrillTile>
  );
}

/**
 * Card on file and reminder e-mail against the drill's rate. The backend
 * only sends a guard when both groups reach the driver minimum.
 */
export function DrillGuardEffectSection({
  effects,
}: {
  effects?: DrillGuardEffects | null;
}) {
  if (!effects?.guards?.length) return null;
  return (
    <>
      {effects.guards.map(g => (
        <GuardTile
          key={g.key}
          guard={g}
          metricKey={effects.metricKey}
          minSample={effects.minSample}
        />
      ))}
    </>
  );
}
